import type { Schluessel } from './einstellungen';

/**
 * ⚠️ **Die einzige Stelle, an der Stage alte Einstellungsnamen und -werte kennt** — eine
 * Übergangshilfe (Entscheidung des Nutzers, 2026-10-08, Variante A).
 *
 * Seit dem 2026-10-08 gilt Single Source of Truth: jede Einstellung hat genau einen Namen, den
 * aus Relays Katalog. Am Draht (`SET_SETTINGS`, Relays QR-Code) nimmt Stage nur noch diese
 * Namen an. Gelesen werden die alten hier nur noch aus den **Ablagen auf dem Gerät** — App- und
 * QR-Ablage, iPad-Einstellungen, MDM-Profil. Die App hat sie selbst dort hineingeschrieben, und
 * MDM-Profile liegen beim Verwalter; ohne diese Datei setzte das Gerät beim nächsten Start
 * still auf Vorgaben zurück (`memory/uebergangshilfen.md`).
 *
 * **Wann sie fallen darf:** erst wenn der Nutzer sagt, dass kein Gerät und kein MDM-Profil mehr
 * alte Namen trägt — nicht, wenn Relay keine mehr schickt. Der Absender sieht nur, was er
 * schickt, nicht, was liegt.
 *
 * Keine Laufzeitabhängigkeit auf `einstellungen.ts` (nur der Typ): die Namen des Katalogs gibt
 * der Aufrufer mit, damit die beiden Dateien sich nicht gegenseitig beim Laden brauchen.
 */

/**
 * ⏳ **Übergang: alte Namen, in jeder Quelle.**
 *
 * Zwei Umbenennungen liegen hinter dem Katalog:
 *
 * - **2026-09-17:** die doppelten Servernamen (`halfPoints`) fielen weg, es galt der Name
 *   dieses Katalogs.
 * - **2026-10-01:** Entscheidung des Nutzers — **Relay vergibt die Namen**, die App benennt um
 *   (`brightness_max` → `max_brightness` usw.; Relay-Spec
 *   `docs/superpowers/specs/2026-10-01-settings-snake-case-design.md`).
 *
 * ⚠️ **Gelesen wird in jeder Quelle, nicht nur in der Ablage.** Ein iPad von vor dem Update
 * trägt die alten Schlüssel in seinen iPad-Einstellungen, ein MDM-Profil schickt sie weiter,
 * ein gedruckter QR-Code ebenso. Ohne diese Tabelle verlöre das Gerät beim ersten Start nach
 * dem Update Serverzugang, Helligkeit und Kommentararten — still, denn die Seite sähe richtig
 * aus.
 *
 * ⚠️ **Der neue Name gewinnt.** Steht beides in einer Quelle, ist der alte ein Überbleibsel.
 *
 * ⚠️ **Unbefristet** (Entscheidung des Nutzers, 2026-10-01): MDM-Profile und gedruckte Codes
 * bleiben lange im Umlauf. Geschrieben wird nur der neue Name. `server_login`/`server_password`
 * waren der **Classic**-Zugang und gehen deshalb auf `classic_*`, nicht auf `communication_*`.
 */
export const ALTE_NAMEN: Partial<Record<Schluessel, readonly string[]>> = {
  server_protocol: ['server_modus'],
  classic_login: ['server_login'],
  classic_password: ['server_password'],
  save_photos_in_album: ['photos_copy'],
  language: ['ui_language'],
  station_comments_enabled: ['sheet_station_comments'],
  text_comments: ['sheet_comment_text'],
  sketch_comments: ['sheet_comment_handwriting'],
  audio_comments: ['sheet_comment_audio'],
  photo_comments: ['sheet_comment_photo'],
  video_comments: ['sheet_comment_video'],
  next_candidate_button: ['submit_scanner'],
  deleted_files_retention_days: ['archive_days', 'archivTage'],
  min_brightness: ['brightness_min'],
  max_brightness: ['brightness_max'],
  sheet_half_points: ['halfPoints'],
  sheet_show_timer: ['showStopWatch'],
  ui_accent: ['colorScheme'],
  video_max_seconds: ['videoMaxSeconds'],
  device_name: ['deviceName'],
};

/** Alle alten Namen — damit die iPad-Seite auch nach ihnen gefragt wird. */
export const ALLE_ALTEN_NAMEN: readonly string[] = Object.values(ALTE_NAMEN).flat() as string[];

/**
 * Eine Quelle in die Namen des Katalogs übersetzen. Unbekanntes fällt weg; der neue Name
 * gewinnt, sonst der erste alte, der etwas trägt.
 */
export function mitNeuenNamen<T>(
  quelle: Record<string, T>,
  katalogNamen: readonly Schluessel[],
): Partial<Record<Schluessel, T>> {
  const ergebnis: Partial<Record<Schluessel, T>> = {};
  for (const name of katalogNamen) {
    if (quelle[name] !== undefined) {
      ergebnis[name] = quelle[name];
      continue;
    }
    for (const alt of ALTE_NAMEN[name] ?? []) {
      if (quelle[alt] !== undefined) {
        ergebnis[name] = quelle[alt];
        break;
      }
    }
  }
  return ergebnis;
}

/**
 * Alte **Werte**, die noch gelesen werden — das Gegenstück zu `ALTE_NAMEN`.
 *
 * Seit dem 2026-10-01 tragen einige Auswahlen Relays Werte: der Server heisst Relay statt
 * METIS, Relays Werte sind klein geschrieben, Erscheinungsbild und Sprache kennen `system`
 * statt `''`, und aus dem Scanner-Schalter wurde eine Auswahl. MDM-Profile, iPad-Einstellungen
 * und gedruckte QR-Codes tragen aber noch die alten Werte. Ohne diese Lesehilfe fiele der Wert
 * durch die Prüfregel und die nächste Quelle spränge ein — im ungünstigen Fall ein `classic`
 * von der iPad-Seite, und das Gerät spräche mit dem falschen Server.
 */
export const ALTE_WERTE: Partial<Record<Schluessel, Record<string, string>>> = {
  server_protocol: { RELAY: 'relay', METIS: 'relay', CLASSIC: 'classic' },
  ui_theme: { '': 'system', hell: 'light', dunkel: 'dark' },
  language: { '': 'system' },
  next_candidate_button: { true: 'scan', false: 'next' },
};

/** Übersetzt einen alten Wert in den heutigen; alles andere bleibt, wie es ist. */
export function neuerWert(schluessel: Schluessel, roh: unknown): unknown {
  const tabelle = ALTE_WERTE[schluessel];
  if (!tabelle || (typeof roh !== 'string' && typeof roh !== 'boolean')) return roh;
  return tabelle[String(roh)] ?? roh;
}

